/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の notes タイムライン 1 行。記入者 · 記入日時のバイライトと、長文を ~140 文字で
 * クランプして「もっと見る / 折りたたむ」でインライン展開する本文を表示する。本文は原文 or 翻訳文を親から
 * 受け取り、React の自動エスケープ + whitespace-pre-wrap でプレーンテキストとして描画する（未検証のユーザー入力）
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体から notes タイムライン 1 行を分離
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { OpsNote } from '../../../types/ops.js';

/** 本文をクランプする最大文字数（超えたら「もっと見る」で展開）。 @graph-connects none */
export const OPS_NOTE_CLAMP_LENGTH = 140;

/** notes タイムライン 1 行の props。 */
export interface OpsNoteRowProps {
  /** 記入者 / 記入日時を読むメモ本体（本文は translated 優先で別途 text から取る）。 */
  note: OpsNote;
  /** 表示する本文（翻訳中は訳文、通常は note.text）。 */
  text: string;
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 記入日時（ISO 8601）を `YYYY-MM-DD HH:mm` の表示形へ整形する（アクティビティ時刻と同様に T を空白に置換）。
 * @graph-connects none
 */
function formatNoteTime(createdAt: string): string {
  return createdAt.slice(0, 16).replace('T', ' ');
}

/**
 * notes タイムライン 1 行。長文はクランプし、「もっと見る / 折りたたむ」でインライン展開する。
 * @graph-connects none
 */
export function OpsNoteRow({ note, text, t }: OpsNoteRowProps): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const isClamped = text.length > OPS_NOTE_CLAMP_LENGTH;
  const shownText = isClamped && !expanded ? `${text.slice(0, OPS_NOTE_CLAMP_LENGTH)}…` : text;
  const byline = t('ops.detail.noteByline', {
    author: note.author ?? t('ops.detail.noteSystemAuthor'),
    time: formatNoteTime(note.createdAt),
  });
  return (
    <div className="space-y-1 border-b border-[var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <div className="text-xs text-[var(--color-text-muted)]">{byline}</div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-secondary)]">
        {shownText}
      </p>
      {isClamped && (
        <button
          type="button"
          className="text-xs text-[var(--color-accent-primary)] hover:underline"
          onClick={() => {
            setExpanded((v) => !v);
          }}
        >
          {expanded ? t('ops.detail.seeLess') : t('ops.detail.seeMore')}
        </button>
      )}
    </div>
  );
}
